import { Button, Checkbox, Divider, FormControlLabel, Grid, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import {
  DefaultFormRefs,
  DefaultFormProps,
} from '../../utils/form-default-props';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { CircularLoading } from '../../../utils';
import { TextFieldSaurus } from '../../../controles/inputs';
import { SelectSaurus } from '../../../controles/selects/select-saurus/select-saurus';
import { makeUtilClasses } from '../../../../theme';
import { useFormConfigLayoutValidation } from './form-config-gerais-validation';
import { ConfigGeraisModel } from '../../../../../services/app/stores/config-gerais';
import { useStyles } from './form-config-gerais-styles';
import classNames from 'classnames';
import { KeyValueModel } from '../../../../../model';

export const FormConfigLayout = React.forwardRef<
  DefaultFormRefs<ConfigGeraisModel>,
  DefaultFormProps<ConfigGeraisModel>
>((props: DefaultFormProps<ConfigGeraisModel>, refs) => {
  const intervalosAudioMock: KeyValueModel[] = [
    new KeyValueModel(5000, '5 segundos'),
    new KeyValueModel(10000, '10 segundos'),
    new KeyValueModel(15000, '15 segundos'),
    new KeyValueModel(20000, '20 segundos'),
    new KeyValueModel(30000, '30 segundos'),
    new KeyValueModel(40000, '40 segundos'),
    new KeyValueModel(50000, '50 segundos'),
    new KeyValueModel(60000, '1 minuto'),
    new KeyValueModel(120000, '2 minutos'),
    new KeyValueModel(180000, '3 minutos'),
    new KeyValueModel(240000, '4 minutos'),
    new KeyValueModel(300000, '5 minutos'),
  ];

  const { FormConfigLayoutValidationYup } = useFormConfigLayoutValidation();
  const utilClasses = makeUtilClasses();
  const classes = useStyles();
  const [, setAtt] = useState<boolean>(false)

  const handleAtt = () => {
    setAtt(prev => !prev);
  }

  const {
    handleSubmit,
    control,
    formState: { errors, touchedFields },
    reset,
    getValues,
    setValue,
    watch
  } = useForm<ConfigGeraisModel>({
    resolver: yupResolver(FormConfigLayoutValidationYup),
    criteriaMode: 'all',
    mode: 'onSubmit',
  });

  const onSubmit = (form: ConfigGeraisModel) => {
    props.onSubmit(form);
  };

  React.useImperativeHandle(refs, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    resetForm: () => {
      reset();
    },
    fillForm: (model: ConfigGeraisModel) => {
      reset({
        ...model,
        habilitarAudioPedidoNovo: model.habilitarAudioPedidoNovo ?? true,
        intervaloAudioPedidoNovoMs: model.intervaloAudioPedidoNovoMs ?? 15000,
      });
      handleAtt()
    },
  }));

  const alterarOpcao = (opcao: number) => {
    let opcoesAtuais: number[] = [];
    if (getValues('visualizarTpPedido'))
      opcoesAtuais = getValues('visualizarTpPedido') as number[];
    setValue('visualizarTpPedido', opcoesAtuais.includes(opcao) ? opcoesAtuais.filter((o) => o !== opcao) : [...opcoesAtuais, opcao])
  }

  return (
    <>
      <div className={utilClasses.formContainer}>
        {(props.loading && props.showLoading) ? (
          <CircularLoading tipo="FULLSIZED" />
        ) : <></>}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={classNames(props.loading && utilClasses.controlLoading, classes.form)}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography color="primary" variant="h6">
                Configuração de Layout
              </Typography>
              <Typography color="textSecondary" variant="body2">
                Configure o layout do KDS conforme as necessidades do seu estabelecimento.
              </Typography>
            </Grid>
            <Grid item xs={6} >
              <Controller
                name="qtdeSetores"
                control={control}
                render={({ field }) => (
                  <TextFieldSaurus
                    tipo="NUMERO"
                    fullWidth={true}
                    showStartAdornment={true}
                    autoComplete={'off'}
                    label="Qtde. de Setores"
                    placeholder="Máximo 4"
                    error={Boolean(
                      errors.qtdeSetores && errors.qtdeSetores.message,
                    )}
                    helperText={
                      touchedFields.qtdeSetores || errors.qtdeSetores
                        ? errors.qtdeSetores?.message
                        : undefined
                    }
                    {...field}
                    value={getValues('qtdeSetores')}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="qtdeProcessos"
                control={control}
                render={({ field }) => (
                  <TextFieldSaurus
                    tipo="NUMERO"
                    fullWidth={true}
                    showStartAdornment={true}
                    autoComplete={'off'}
                    label="Qtde. de Processos"
                    placeholder="Máximo 4"
                    error={Boolean(
                      errors.qtdeProcessos && errors.qtdeProcessos.message,
                    )}
                    helperText={
                      touchedFields.qtdeProcessos || errors.qtdeProcessos
                        ? errors.qtdeProcessos?.message
                        : undefined
                    }
                    {...field}
                    value={getValues('qtdeProcessos')}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider className={classes.divider} />
            </Grid>
            <Grid item xs={12}>
              <Typography color="primary" variant="h6">
                Visualizar Pedidos Local ou Delivery
              </Typography>
              <Typography color="textSecondary" variant="body2">
                Selecione se deseja visualizar apenas pedidos locais ou também pedidos de Entrega/Retirada.
              </Typography>
            </Grid>
            <Grid item xs={12} >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={watch('visualizarTpPedido')?.some((o) => o === 0) ?? false}
                    onChange={() => alterarOpcao(0)}
                  />
                }
                label="Visualizar Pedidos Locais"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={watch('visualizarTpPedido')?.some((o) => o === 1) ?? false}
                    onChange={() => alterarOpcao(1)}
                  />
                }
                label="Visualizar Pedidos de Entrega/Retirada"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider className={classes.divider} />
            </Grid>
            <Grid item xs={12}>
              <Typography color="primary" variant="h6">
                Alerta Sonoro de Novo Pedido
              </Typography>
              <Typography color="textSecondary" variant="body2">
                Habilite para tocar um som quando houver novos pedidos.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="habilitarAudioPedidoNovo"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(_, checked) => field.onChange(checked)}
                      />
                    }
                    label="Habilitar áudio para pedidos novos"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="intervaloAudioPedidoNovoMs"
                control={control}
                render={({ field }) => (
                  <SelectSaurus
                    conteudo={intervalosAudioMock}
                    fullWidth={true}
                    label="Intervalo do alerta sonoro"
                    error={Boolean(
                      errors.intervaloAudioPedidoNovoMs && errors.intervaloAudioPedidoNovoMs.message,
                    )}
                    helperText={
                      touchedFields.intervaloAudioPedidoNovoMs || errors.intervaloAudioPedidoNovoMs
                        ? errors.intervaloAudioPedidoNovoMs?.message
                        : 'Define de quanto em quanto tempo o áudio repete enquanto houver pedidos pendentes.'
                    }
                    {...field}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    value={Number(field.value ?? 15000)}
                    disabled={!watch('habilitarAudioPedidoNovo')}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Button style={{ display: 'none' }} type="submit"></Button>
        </form>
      </div>
    </>
  );
});
